import React, { useContext, useMemo } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { ForecasterPurposeInputsContext } from './ForecasterPurposeInputsContext';

import {
  ForecasterPurposeProjectionDocument,
  ForecasterPurposeProjectionQuery,
  ForecasterPurposeProjectionQueryVariables,
} from '../graphql/forecasterPurposeProjection.generated';

const ForecasterPurposeProjectionContext = React.createContext<{
  purpose?: ForecasterPurposeProjectionQuery['purpose'];
  isLoading: boolean;
}>({ purpose: undefined, isLoading: false });

export const ForecasterPurposeProjectionProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const purposeId = useRequiredParam('purposeId');
  const { projectionQueryInputs } = useContext(ForecasterPurposeInputsContext) || {};

  const { majorPurchase, emergencyFund, retirementGrowth, retirementIncome } = projectionQueryInputs || {};
  const variables: ForecasterPurposeProjectionQueryVariables = {
    purposeId,
    majorPurchaseProjectionInput: majorPurchase && {
      adHocInflow: majorPurchase.adHocInflow,
      monthlyRecurringInflows: majorPurchase.monthlyRecurringDeposits,
    },
    emergencyFundProjectionInput: emergencyFund && {
      adHocInflow: emergencyFund.adHocInflow,
      monthlyRecurringInflows: emergencyFund.monthlyRecurringDeposits,
    },
    retirementGrowthProjectionInput: retirementGrowth && {
      annualRecurringInflows: retirementGrowth.annualRecurringDeposits,
      retirementAge: retirementGrowth.retirementAge,
      retirementZip: retirementGrowth.retirementZip,
      targetMonthlySpendingAmount: retirementGrowth.targetMonthlySpendingAmount,
    },
    retirementIncomeProjectionInput: retirementIncome && {
      adHocInflow: retirementIncome.adHocInflow,
      monthlyRecurringOutflows: retirementIncome.monthlyRecurringWithdrawals,
    },
  };

  const { data, loading, previousData } = useGraphQLQuery(ForecasterPurposeProjectionDocument, {
    variables,
    skip: projectionQueryInputs === undefined,
    throwOnError: true,
  });

  const purpose = data?.purpose || previousData?.purpose;

  const value = useMemo(() => ({ purpose, isLoading: loading }), [purpose, loading]);

  return (
    <ForecasterPurposeProjectionContext.Provider value={value}>{children}</ForecasterPurposeProjectionContext.Provider>
  );
};

export const useForecasterPurposeProjection = () => useContext(ForecasterPurposeProjectionContext);
export const useMajorPurchaseProjection = () => {
  const { purpose, isLoading } = useForecasterPurposeProjection();
  if (!purpose || purpose.__typename === 'PurposeMajorPurchase') return { purpose, isLoading };
  throw new Error(`Attempted to query for a purpose with typename PurposeMajorPurchase but got ${purpose.__typename}`);
};
export const useEmergencyFundProjection = () => {
  const { purpose, isLoading } = useForecasterPurposeProjection();
  if (!purpose || purpose.__typename === 'PurposeEmergencyFund') return { purpose, isLoading };
  throw new Error(`Attempted to query for a purpose with typename PurposeEmergencyFund but got ${purpose.__typename}`);
};
export const useRetirementGrowthProjection = () => {
  const { purpose, isLoading } = useForecasterPurposeProjection();
  if (!purpose || purpose.__typename === 'PurposeRetirementGrowth') return { purpose, isLoading };
  throw new Error(
    `Attempted to query for a purpose with typename PurposeRetirementGrowth but got ${purpose?.__typename}`
  );
};
export const useRetirementIncomeProjection = () => {
  const { purpose, isLoading } = useForecasterPurposeProjection();
  if (!purpose || purpose.__typename === 'PurposeRetirementIncome') return { purpose, isLoading };
  throw new Error(
    `Attempted to query for a purpose with typename PurposeRetirementIncome but got ${purpose.__typename}`
  );
};
